import { registerPlugin, Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { isNative, platform } from './is-native';

/**
 * True only on a native binary that actually ships the IAP plugin. Binaries
 * released before IAP return false, so the client keeps the pre-IAP experience
 * (no in-app signup, no paywall) — the client half of the release gate. Pairs
 * with the `MuckaIAP` UA token / `X-Mucka-Iap` header on the server.
 */
export function hasIapPlugin(): boolean {
  return isNative() && Capacitor.isPluginAvailable('MuckaIap');
}

/**
 * JS bridge for native in-app purchases — the solo-tier subscription sold
 * through StoreKit 2 (iOS) and Play Billing (Android). Pairs with
 * `ios/App/App/MuckaIapPlugin.swift` and
 * `android/app/src/main/java/ai/mucka/MuckaIapPlugin.kt` (added in the native
 * pass; both expose the identical contract below).
 *
 * Flow: the paywall (shown to a native locked owner at trial expiry) calls
 * `purchase()`; the plugin runs the store purchase sheet and returns a signed
 * proof (Apple: the JWS-signed StoreKit 2 transaction; Google: the purchase
 * token) which we POST to `/api/iap/{apple|google}/verify` for server-side
 * verification and entitlement. Renewals thereafter come via the store
 * notification webhooks, not this bridge.
 *
 * On web every method is a safe no-op / rejection — there is no store to talk
 * to, and the web billing path (Stripe) is entirely separate.
 */

export type SoloPeriod = 'monthly' | 'quarterly';

/** Product ids as configured in App Store Connect / Play Console, per billing
 *  period. (Apple uses reverse-DNS ids; Play uses lower_snake_case.) */
export const SOLO_PRODUCT_IDS: Record<'ios' | 'android', Record<SoloPeriod, string>> = {
  ios: { monthly: 'ai.mucka.solo.monthly', quarterly: 'ai.mucka.solo.quarterly' },
  android: { monthly: 'mucka_solo_monthly', quarterly: 'mucka_solo_quarterly' },
};

export function soloProductId(period: SoloPeriod = 'monthly'): string | null {
  const p = platform();
  return p === 'ios' || p === 'android' ? SOLO_PRODUCT_IDS[p][period] : null;
}

export interface IapProduct {
  productId: string;
  displayName: string;
  /** Localised price string straight from the store, e.g. "£29.00". */
  priceString: string;
}

export interface IapPurchaseProof {
  platform: 'ios' | 'android';
  productId: string;
  /** iOS: the JWS-signed StoreKit 2 transaction. */
  transactionJws?: string;
  /** iOS: StoreKit environment ("Production" | "Sandbox") for the verifier. */
  environment?: string;
  /** Android: the Play Billing purchase token. */
  purchaseToken?: string;
}

interface MuckaIapPlugin {
  getProducts(opts: { productIds: string[] }): Promise<{ products: IapProduct[] }>;
  /**
   * `accountId` is the business the purchase pays for. Both plugins stamp it on
   * the purchase (Apple appAccountToken / Play obfuscated account id), so the
   * store itself says which business owns the subscription.
   */
  purchase(opts: { productId: string; accountId?: string }): Promise<IapPurchaseProof>;
  restorePurchases(): Promise<{ entitlement: IapPurchaseProof | null }>;
  getActiveEntitlement(): Promise<{ entitlement: IapPurchaseProof | null }>;
  /** Deep-link the OS "manage subscriptions" surface (StoreKit
   *  showManageSubscriptions / Play subscriptions deep link). */
  openManageSubscriptions(): Promise<void>;
  /** Fired once per purchase, when the store's own sheet is on screen. */
  addListener(eventName: 'storeSheetOpened', listenerFunc: () => void): Promise<PluginListenerHandle>;
}

const MuckaIap = registerPlugin<MuckaIapPlugin>('MuckaIap');

/** Fetch both period products in one call, so the paywall can show the
 *  monthly/quarterly toggle with real prices. Missing ones come back null. */
export async function getSoloProducts(): Promise<Record<SoloPeriod, IapProduct | null>> {
  const empty = { monthly: null, quarterly: null };
  if (!isNative()) return empty;
  const p = platform();
  if (p !== 'ios' && p !== 'android') return empty;
  const ids = SOLO_PRODUCT_IDS[p];
  try {
    const { products } = await MuckaIap.getProducts({ productIds: [ids.monthly, ids.quarterly] });
    const byId = (id: string) => products.find((x) => x.productId === id) ?? null;
    return { monthly: byId(ids.monthly), quarterly: byId(ids.quarterly) };
  } catch (err) {
    console.warn('[iap] getProducts failed:', err instanceof Error ? err.message : err);
    return empty;
  }
}

/** Run the store purchase sheet for the chosen period. Rejects on web and when
 *  the product id can't be resolved. Returns the proof to send to verify. */
export async function purchaseSolo(
  period: SoloPeriod = 'monthly',
  /** The business this pays for. Each workspace pays for itself. */
  accountId?: string,
  /**
   * Called when the store's sheet is on screen. The page cannot see it: Google's
   * sheet is a translucent overlay, so the WebView stays 'visible' underneath
   * (MUCKA-WEB-AG). A binary that predates the event never calls it.
   */
  opts: { onSheetOpened?: () => void } = {},
): Promise<IapPurchaseProof> {
  if (!isNative()) throw new Error('In-app purchase is only available in the app.');
  const productId = soloProductId(period);
  if (!productId) throw new Error('No purchasable product on this platform.');
  let handle: PluginListenerHandle | null = null;
  if (opts.onSheetOpened) {
    const onSheetOpened = opts.onSheetOpened;
    handle = await MuckaIap.addListener('storeSheetOpened', () => onSheetOpened()).catch(() => null);
  }
  try {
    return await MuckaIap.purchase({ productId, accountId });
  } finally {
    void handle?.remove();
  }
}

/** Restore a previously-purchased subscription (same store account). Returns the
 *  active entitlement proof, or null if there's nothing to restore. */
export async function restorePurchases(): Promise<IapPurchaseProof | null> {
  if (!isNative()) return null;
  try {
    const { entitlement } = await MuckaIap.restorePurchases();
    return entitlement;
  } catch (err) {
    console.warn('[iap] restorePurchases failed:', err instanceof Error ? err.message : err);
    return null;
  }
}

/** The current active store entitlement, if any — used by the launch-time
 *  reconcile so a missed webhook / reinstall / new-device sign-in self-heals. */
export async function getActiveEntitlement(): Promise<IapPurchaseProof | null> {
  if (!isNative()) return null;
  try {
    const { entitlement } = await MuckaIap.getActiveEntitlement();
    return entitlement;
  } catch (err) {
    console.warn('[iap] getActiveEntitlement failed:', err instanceof Error ? err.message : err);
    return null;
  }
}

/** Open the OS-native "manage subscriptions" screen (cancel / change plan). */
export async function openManageSubscriptions(): Promise<void> {
  if (!isNative()) return;
  try {
    await MuckaIap.openManageSubscriptions();
  } catch (err) {
    console.warn('[iap] openManageSubscriptions failed:', err instanceof Error ? err.message : err);
  }
}

/**
 * Best-effort reconcile on app launch: if the store reports an active
 * entitlement, tell the server so a missed webhook / reinstall / new-device
 * sign-in self-heals. No-ops on web, when there's nothing to restore, or when
 * the user isn't signed in yet (the reconcile route 401s harmlessly).
 */
export async function reconcileEntitlementOnLaunch(): Promise<void> {
  if (!isNative()) return;
  try {
    const proof = await getActiveEntitlement();
    if (!proof) return;
    await fetch('/api/iap/reconcile', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(proof),
    });
  } catch (err) {
    console.warn('[iap] launch reconcile failed:', err instanceof Error ? err.message : err);
  }
}
